/**
 * FERP Portal - Lens Catalog Page
 *
 * Config type ln-: Lenses filter constellation visibility by role, scope,
 * setting, or situation (DRR-135). Each lens defines which ring nodes are
 * highlighted, de-emphasized, shown, or hidden for a given context.
 *
 * Architecture:
 *   - Backend: lensTemplates query in scope_api
 *   - Config source: common/configs/seed/lenses/ln-*
 *   - DRR-135: User-switchable traversal lens
 *   - DRR-075: Flat SK — CFG#LENS#ln-*
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend lensTemplates query
 * - No hardcoded field values
 * - Admin view layer only
 */

import { useState, useMemo } from 'react';
import { useLensTemplates } from '@/hooks/useLensTemplates';
import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconEye,
  IconFilter,
  IconInfoCircle,
  IconSearch,
  IconShieldCheck,
} from '@tabler/icons-react';
import type { LensTemplate } from '@/generated/graphql';

const PHASE_COLORS: Record<string, string> = {
  role: 'blue',
  scope: 'violet',
  setting: 'teal',
  situation: 'orange',
};

const SAFETY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
};

export function LensCatalogPage() {
  const { templates: lensTemplates, loading, error } = useLensTemplates();
  const [search, setSearch] = useState('');

  const lenses = useMemo(() => {
    const all = lensTemplates;
    if (!search) return all;
    const lower = search.toLowerCase();
    return all.filter(
      (l) =>
        l.name.toLowerCase().includes(lower) ||
        l.lensId.toLowerCase().includes(lower) ||
        l.phase.toLowerCase().includes(lower) ||
        l.verticals.some((v) => v.toLowerCase().includes(lower)),
    );
  }, [lensTemplates, search]);

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Lenses">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Lens Catalog</Title>
            <Text c="dimmed" size="sm">
              Config type ln- — traversal lenses that filter constellation visibility by role, scope, setting, or situation (DRR-135)
            </Text>
          </div>
          <Badge variant="light" color="violet" size="lg">
            {lenses.length} Lenses
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="About Lenses">
          Lenses control what users see in constellation views. A DON lens highlights clinical
          nodes; a billing lens highlights financial nodes. Users switch lenses via the
          constellation toolbar. Active lenses for the tenant are configured in the Setting bundle.
        </Alert>

        <TextInput
          placeholder="Search by name, ID, phase, or vertical..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        <Stack gap="sm">
          {lenses.map((lens) => (
            <Card key={lens.lensId} withBorder p="md">
              <Group justify="space-between" mb="sm">
                <Group gap="sm">
                  <ThemeIcon variant="light" color={PHASE_COLORS[lens.phase] ?? 'gray'} size="sm">
                    {lens.phase === 'role' ? <IconEye size={14} /> : <IconFilter size={14} />}
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{lens.name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{lens.lensId}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Badge variant="light" color={PHASE_COLORS[lens.phase] ?? 'gray'} size="sm">
                    {lens.phase}
                  </Badge>
                  {lens.safetyLevel && (
                    <Badge variant="outline" color={SAFETY_COLORS[lens.safetyLevel] ?? 'gray'} size="xs">
                      <Group gap={4}>
                        <IconShieldCheck size={10} />
                        {lens.safetyLevel}
                      </Group>
                    </Badge>
                  )}
                  <Badge variant="outline" size="xs" color="gray">
                    v{lens.version}
                  </Badge>
                </Group>
              </Group>

              {lens.description && (
                <Text size="sm" c="dimmed" mb="sm">{lens.description}</Text>
              )}

              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed">Rules</Text>
                  <Text size="sm" fw={500}>{lens.ruleCount}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Verticals</Text>
                  <Group gap={4}>
                    {lens.verticals.map((v) => (
                      <Badge key={v} variant="outline" size="xs" color="gray">{v}</Badge>
                    ))}
                  </Group>
                </div>
                {lens.appliesToConstellation && (
                  <div>
                    <Text size="xs" c="dimmed">Target Constellation</Text>
                    <Text size="xs" ff="monospace">{lens.appliesToConstellation}</Text>
                  </div>
                )}
              </SimpleGrid>
            </Card>
          ))}
        </Stack>

        {!loading && lenses.length === 0 && (
          <Text c="dimmed" ta="center" py="xl">
            {search ? 'No lenses match your search.' : 'No lenses configured for this tenant.'}
          </Text>
        )}
      </Stack>
    </Container>
  );
}

export default LensCatalogPage;
